import React from 'react';
import { CompositeDecorator, EditorState, convertFromRaw } from 'draft-js';
import { convertToHTML } from 'draft-convert';

// TODO: convert the rest of these legacy css classes to tailwind

export const checkIfRichText = (richObj) => {
  return richObj?.blocks?.some((block => block.text && block.text.length > 0))
}

const Block = {
  UNSTYLED: 'unstyled',
  PARAGRAPH: 'unstyled',
  OL: 'ordered-list-item',
  UL: 'unordered-list-item',
  H1: 'header-one',
  H2: 'header-two',
  H3: 'header-three',
  H4: 'header-four',
  H5: 'header-five',
  H6: 'header-six',
  CODE: 'code-block',
  BLOCKQUOTE: 'blockquote',
  PULLQUOTE: 'pullquote',
  ATOMIC: 'atomic',
  BLOCKQUOTE_CAPTION: 'block-quote-caption',
  CAPTION: 'caption',
  TODO: 'todo',
  IMAGE: 'atomic:image',
  BREAK: 'atomic:break',
};

const Inline = {
  BOLD: 'BOLD',
  CODE: 'CODE',
  ITALIC: 'ITALIC',
  STRIKETHROUGH: 'STRIKETHROUGH',
  UNDERLINE: 'UNDERLINE',
  HIGHLIGHT: 'HIGHLIGHT',
  SUPERSCRIPT: 'SUPERSCRIPT',
  SUBSCRIPT: 'SUBSCRIPT',
};

const Entity = {
  LINK: 'LINK',
};
const styles = {
  root: {
    fontFamily: '\'Georgia\', serif'
    , padding: 20
    , width: 600
  },
  buttons: {
    marginBottom: 10
  },
  urlInputContainer: {
    marginBottom: 10
  },
  urlInput: {
    fontFamily: '\'Georgia\', serif'
    , marginRight: 10
    , padding: 3
    , width: '30%'
  },
  editor: {
    border: '1px solid #ccc'
    , cursor: 'text'
    , minHeight: 80
    , padding: 10
  },
  button: {
    marginTop: 10
    , textAlign: 'center'
  },
  link: {
    color: '#3b5998'
    , textDecoration: 'underline'
  },
};

const richContentUtils = {

  editorState(content, customFontMap) {
    // render Draft content
    const rawContent = Object.assign({}, content);
    if(!rawContent.entityMap) {
      rawContent.entityMap = {};
    }
    const Link = (props) => {
      const { url } = props.contentState.getEntity(props.entityKey).getData();
      return (
        <a href={url} style={styles.link}>
          {props.children}
        </a>
      );
    };
    const decorator = new CompositeDecorator([
      {
        strategy: richContentUtils._findLinkEntities
        , component: Link
      }
    ]);
    let currentContent = EditorState.createWithContent(convertFromRaw(rawContent), decorator).getCurrentContent();
    return this._renderHTML(currentContent, customFontMap);
  }

  , _findLinkEntities(contentBlock, callback, contentState) {
    contentBlock.findEntityRanges(
      (character) => {
        const entityKey = character.getEntity();
        return (
          entityKey !== null &&
          contentState.getEntity(entityKey).getType() === 'LINK'
        );
      },
      callback
    );
  }

  // converting to HTML functions
  , _renderHTML(contentState, customFontMap) {
    return convertToHTML({
      styleToHTML: (style) => {
        switch (style) {
          case Inline.BOLD:
            return <strong className={`font-semibold`} />;
          case Inline.CODE:
            return <code className={``} />;
          case Inline.ITALIC:
            return <em className={``} />;
          case Inline.STRIKETHROUGH:
            return <strike className={``} />;
          case Inline.UNDERLINE:
            return <u className={``} />;
          case Inline.HIGHLIGHT:
            return <span className={``} />;
          case Inline.SUPERSCRIPT:
            return <sup className={`text-xs align-super`} />;
          case Inline.SUBSCRIPT:
            return <sub className={`text-xs align-sub`} />;
          default:
            return null;
        }
      }
      , blockToHTML: (block) => {
        const blockType = block.type;
        const data = block.data;

        const alignment = data['text-align'] || 'left';
        // return the whole strings here so tailwind includes the classes in the bundle
        const alignmentStyle = (() => {
          switch (alignment) {
            case 'left':
              return 'text-left';
            case 'center':
              return 'text-center';
            case 'right':
              return 'text-right';
            case 'justify':
              return 'text-justify';
            default:
              return '';
          }
        })();

        switch (blockType) {
          case Block.H1:
            // eslint-disable-next-line jsx-a11y/heading-has-content
            return customFontMap && customFontMap["h1"] ? 
            <h1 className={`${customFontMap["h1"]} ${alignmentStyle}`}/>
            :
            <h1 className={`text-4xl font-semibold font-sans ${alignmentStyle}`} />;
          case Block.H2:
            // eslint-disable-next-line jsx-a11y/heading-has-content
            return customFontMap && customFontMap["h2"] ? 
            <h2 className={`${customFontMap["h2"]} ${alignmentStyle}`}/>
            :
            <h2 className={`text-3xl font-semibold font-sans ${alignmentStyle}`} />;
          case Block.H3:
            // eslint-disable-next-line jsx-a11y/heading-has-content
            return customFontMap && customFontMap["h3"] ? 
            <h3 className={`${customFontMap["h3"]} ${alignmentStyle}`}/>
            :
            <h3 className={`text-2xl font-semibold font-sans ${alignmentStyle}`} />;
          case Block.H4:
            // eslint-disable-next-line jsx-a11y/heading-has-content
            return <h4 className={``} />;
          case Block.H5:
            // eslint-disable-next-line jsx-a11y/heading-has-content
            return <h5 className={``} />;
          case Block.H6:
            // eslint-disable-next-line jsx-a11y/heading-has-content
            return <h6 className={``} />;
          case Block.BLOCKQUOTE_CAPTION:
          case Block.CAPTION:
            return {
              start: `<p class=""><caption>`,
              end: '</caption></p>',
            };
          case Block.IMAGE: {
            const imgData = block.data;
            const text = block.text;
            const extraClass = (text.length > 0 ? ' md-block-image-has-caption' : '');
            return {
              start: `<figure class="md-block-image${extraClass}"><img src="${imgData.src}" alt="${block.text}" /><figcaption className="md-block-image-caption">`,
              end: '</figcaption></figure>',
            };
          }
          case Block.ATOMIC:
            return {
              start: `<figure className="">`,
              end: '</figure>',
            };
          case Block.TODO: {
            const checked = block.data.checked || false;
            let inp = '';
            let containerClass = '';
            if (checked) {
              inp = '<input type=checkbox disabled checked="checked" />';
              containerClass = 'md-block-todo-checked';
            } else {
              inp = '<input type=checkbox disabled />';
              containerClass = 'md-block-todo-unchecked';
            }
            return {
              start: `<div class=" ${containerClass}">${inp}<p>`,
              end: '</p></div>',
            };
          }
          case Block.BREAK:
            return <hr className={``} />;
          case Block.BLOCKQUOTE:
            return <blockquote className={`text-inherit text-base`} />;
          case Block.OL:
            return {
              element: <li />,
              nest: <ol className={`list-decimal list-inside text-inherit text-base ${customFontMap && customFontMap['ol'] ? customFontMap['ol'] : ''}`} />,
            };
          case Block.UL:
            return {
              element: <li />,
              nest: <ul className={`list-disc list-inside text-inherit text-base ${customFontMap && customFontMap['ul'] ? customFontMap['ul'] : ''}`} />
            };
          case Block.UNSTYLED:
            if (block.text.length < 1) {
              return <p className={`text-inherit text-base ${alignmentStyle}`}><br /></p>;
            }
            return customFontMap && customFontMap["p"] ? 
            <p className={`${customFontMap["p"]} ${alignmentStyle}`}/>
            :
            <p className={`text-inherit text-base ${alignmentStyle}`} />;
          // for the default case we return a div with the alignment class, returning null will crash the app!
          default: return <div className={`${alignmentStyle}`} />;
        }
      }
      , entityToHTML: (entity, originalText) => {
        if(entity.type === Entity.LINK) {
          return `<a class="underline text-base" href="${entity.data.url}" target="_blank" rel="noopener noreferrer">${originalText}</a>`;
        }
        if(entity.type === 'image' || entity.type === 'IMAGE') {
          return `<img src="${entity.data.src}" alt="${originalText}" />`;
        }
        return originalText;
      }
      
    })(contentState);
  }

}

export default richContentUtils;
